import { JobPosition, JobSearchFilters, UserProfile } from '../types/job';

// Mock job database - In production, this would come from an API
const mockJobs: JobPosition[] = [
  {
    id: '1',
    title: 'Senior Software Engineer',
    company: 'Google',
    description: 'Looking for an experienced Software Engineer to join our team.',
    skills: ['JavaScript', 'TypeScript', 'React', 'Node.js'],
    experience: 5,
    industry: 'Technology',
    location: 'San Francisco, CA',
    jobType: 'hybrid',
    salary: { min: 150000, max: 200000, currency: 'USD' },
    matchScore: 0,
  },
  {
    id: '2',
    title: 'Full Stack Developer',
    company: 'Microsoft',
    description: 'Join our team as a Full Stack Developer.',
    skills: ['C#', 'React', 'Azure', 'SQL'],
    experience: 3,
    industry: 'Technology',
    location: 'Seattle, WA',
    jobType: 'hybrid',
    salary: { min: 120000, max: 160000, currency: 'USD' },
    matchScore: 0,
  },
  {
    id: '3',
    title: 'AI/ML Engineer',
    company: 'OpenAI',
    description: 'Help us build the future of AI.',
    skills: ['Python', 'Machine Learning', 'TensorFlow', 'PyTorch'],
    experience: 4,
    industry: 'Technology',
    location: 'Remote',
    jobType: 'remote',
    salary: { min: 160000, max: 220000, currency: 'USD' },
    matchScore: 0,
  },
  {
    id: '4',
    title: 'Product Manager',
    company: 'Airbnb',
    description: 'Drive product strategy and execution.',
    skills: ['Product Strategy', 'Data Analysis', 'Leadership', 'Communication'],
    experience: 6,
    industry: 'Technology',
    location: 'San Francisco, CA',
    jobType: 'onsite',
    salary: { min: 140000, max: 180000, currency: 'USD' },
    matchScore: 0,
  },
  {
    id: '5',
    title: 'DevOps Engineer',
    company: 'Amazon',
    description: 'Manage infrastructure and deployment pipelines.',
    skills: ['AWS', 'Docker', 'Kubernetes', 'Python'],
    experience: 4,
    industry: 'Technology',
    location: 'Remote',
    jobType: 'remote',
    salary: { min: 130000, max: 170000, currency: 'USD' },
    matchScore: 0,
  },
];

/**
 * Calculate match score between user profile and job position
 */
export const calculateMatchScore = (userProfile: UserProfile, job: JobPosition): number => {
  let score = 0;

  // Skills match (40%)
  const matchingSkills = job.skills.filter((skill) =>
    userProfile.skills.some((userSkill) =>
      userSkill.toLowerCase().includes(skill.toLowerCase()) ||
      skill.toLowerCase().includes(userSkill.toLowerCase())
    )
  );
  const skillsScore = (matchingSkills.length / job.skills.length) * 40;
  score += skillsScore;

  // Experience match (30%)
  const experienceDiff = Math.abs(userProfile.experience - job.experience);
  const experienceScore = Math.max(0, 30 - experienceDiff * 5);
  score += experienceScore;

  // Industry match (15%)
  if (userProfile.industry && job.industry.toLowerCase() === userProfile.industry.toLowerCase()) {
    score += 15;
  }

  // Location preference match (15%)
  if (userProfile.preferredLocations) {
    if (job.jobType === 'remote' && userProfile.preferredJobTypes?.includes('remote')) {
      score += 15;
    } else if (
      userProfile.preferredLocations.some((loc) =>
        job.location.toLowerCase().includes(loc.toLowerCase())
      )
    ) {
      score += 15;
    }
  }

  return Math.min(100, Math.round(score));
};

/**
 * Search for jobs based on filters and user profile
 */
export const searchJobs = (
  userProfile: UserProfile,
  filters?: JobSearchFilters
): JobPosition[] => {
  let results = [...mockJobs];

  // Filter by keyword
  if (filters?.keyword) {
    const keyword = filters.keyword.toLowerCase();
    results = results.filter(
      (job) =>
        job.title.toLowerCase().includes(keyword) ||
        job.company.toLowerCase().includes(keyword) ||
        job.description.toLowerCase().includes(keyword)
    );
  }

  // Filter by skills
  if (filters?.skills && filters.skills.length > 0) {
    results = results.filter((job) =>
      filters.skills!.some((skill) =>
        job.skills.some((jobSkill) =>
          jobSkill.toLowerCase().includes(skill.toLowerCase())
        )
      )
    );
  }

  // Filter by experience
  if (filters?.experience) {
    results = results.filter(
      (job) =>
        job.experience >= filters.experience!.min &&
        job.experience <= filters.experience!.max
    );
  }

  // Filter by industry
  if (filters?.industry) {
    results = results.filter((job) =>
      job.industry.toLowerCase() === filters.industry!.toLowerCase()
    );
  }

  // Filter by location
  if (filters?.location) {
    results = results.filter((job) =>
      job.location.toLowerCase().includes(filters.location!.toLowerCase())
    );
  }

  // Filter by job type
  if (filters?.jobType && filters.jobType !== 'all') {
    results = results.filter((job) => job.jobType === filters.jobType);
  }

  // Filter by salary range
  if (filters?.salaryRange) {
    results = results.filter(
      (job) =>
        job.salary &&
        job.salary.max >= filters.salaryRange!.min &&
        job.salary.min <= filters.salaryRange!.max
    );
  }

  // Calculate match scores
  results = results.map((job) => ({
    ...job,
    matchScore: calculateMatchScore(userProfile, job),
  }));

  // Sort by match score (descending)
  results.sort((a, b) => b.matchScore - a.matchScore);

  return results;
};

/**
 * Get recommended jobs based on user profile only
 */
export const getRecommendedJobs = (userProfile: UserProfile): JobPosition[] => {
  return searchJobs(userProfile);
};
